import { TypedDocumentNode, gql } from '@apollo/client';
import styled from 'styled-components';

import { RivalsLogo } from '@sorare/core/src/atoms/icons/RivalsLogo';
import { ConditionalWrapper } from '@sorare/core/src/atoms/layout/ConditionalWrapper';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { HeadlineXL, LabelS } from '@sorare/core/src/atoms/typography';
import { Color } from '@sorare/core/src/style/types';

import { GridBackground } from '@sorare/gameplay/src/components/rivals/GridBackground';

import { GroupAvatar } from 'components/rivalsUserGroup/GroupAvatar';
import { MembersRecap } from 'components/rivalsUserGroup/MembersRecap';

import { ToMembersList } from './ToMembersList';
import { GroupOverview_rivalsUserGroup } from './__generated__/index.graphql';

const Wrapper = styled.div`
  overflow: hidden;
  width: 100%;
`;
const InnerWrapper = styled(Vertical)`
  padding: var(--double-unit) 0;
`;
const Subtitle = styled(LabelS).attrs({ bold: true })`
  color: var(--c-rivals-purple-600);
  text-transform: uppercase;
`;

type Props = {
  userGroup: Nullable<GroupOverview_rivalsUserGroup>;
  bgColor?: Color;
  subtitle?: React.ReactNode;
  withRivalsLogo?: boolean;
  withMembersRecap?: boolean;
  children?: React.ReactNode;
  enableMembersList?: boolean;
};

export const GroupOverview = ({
  userGroup,
  bgColor,
  subtitle,
  withRivalsLogo,
  withMembersRecap = true,
  children,
  enableMembersList,
}: Props) => {
  return (
    <Wrapper>
      <GridBackground height="auto" color="#454545" bgColor={bgColor}>
        <InnerWrapper center>
          {withRivalsLogo && <RivalsLogo width={80} />}
          <GroupAvatar userGroup={userGroup} />
          {subtitle && <Subtitle>{subtitle}</Subtitle>}
          <HeadlineXL>{userGroup?.displayName}</HeadlineXL>
          {withMembersRecap && userGroup && (
            <ConditionalWrapper
              wrap={!!enableMembersList}
              Wrapper={ToMembersList}
              props={{
                userGroupSlug: userGroup.slug,
              }}
            >
              <MembersRecap userGroup={userGroup} bgColor={bgColor} />
            </ConditionalWrapper>
          )}
        </InnerWrapper>
        {children}
      </GridBackground>
    </Wrapper>
  );
};

GroupOverview.fragments = {
  rivalsUserGroup: gql`
    fragment GroupOverview_rivalsUserGroup on RivalsUserGroup {
      slug
      displayName
      ...GroupAvatar_rivalsUserGroup
      ...MembersRecap_rivalsUserGroup
    }
    ${MembersRecap.fragments.rivalsUserGroup}
    ${GroupAvatar.fragments.rivalsUserGroup}
  ` as TypedDocumentNode<GroupOverview_rivalsUserGroup>,
};
